import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import companyService from '../services/companyService';

/**
 * ------------------------------------------------------------------
 * INDICE DE FUNCIONES Y ESTRUCTURA
 * ------------------------------------------------------------------
 * 1. Context Creation .................... Creación del contexto
 * 2. Custom Hook (useCompany) ............ Hook para usar los datos de la empresa
 * 3. Provider (CompanyProvider) .......... Componente proveedor
 *    - State ............................. Estados (company, loading, error)
 *    - fetchCompany ...................... Cargar datos de la empresa (API/Local)
 *    - createCompany ..................... Crear nueva empresa
 *    - updateCompany ..................... Actualizar empresa existente
 *    - getCompanyRif ..................... Obtener RIF actual
 * ------------------------------------------------------------------
 */

/**
 * ------------------------------------------------------------------
 * 1. Context Creation
 * ------------------------------------------------------------------
 */
const CompanyContext = createContext();

/**
 * ------------------------------------------------------------------
 * 2. Custom Hook (useCompany)
 * ------------------------------------------------------------------
 */
export const useCompany = () => {
    const context = useContext(CompanyContext);
    if (!context) {
        throw new Error('useCompany must be used within a CompanyProvider');
    }
    return context;
};

/**
 * ------------------------------------------------------------------
 * 3. Provider (CompanyProvider)
 * ------------------------------------------------------------------
 */
export const CompanyProvider = ({ children }) => {
    const { isAuthenticated, loading: authLoading } = useAuth();

    // State
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // fetchCompany
    const fetchCompany = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // Obtener todas las empresas y usar la primera
            const companies = await companyService.getAll();
            if (companies && companies.length > 0) {
                const active = companies[0];
                setCompany(active);
                // localStorage.setItem('activeCompany', JSON.stringify(active)); // REMOVE: Using Context state only
            } else {
                console.log('No companies found');
                setCompany(null);
                // localStorage.removeItem('activeCompany');
            }
        } catch (err) {
            console.error('Error fetching company:', err);
            setError(err.message || 'Error al cargar datos de la empresa');
        } finally {
            setLoading(false);
        }
    }, []);

    // createCompany
    const createCompany = async (companyData) => {
        console.log('Creating company:', companyData);
        setLoading(true);
        setError(null);
        try {
            const newCompany = await companyService.create(companyData);
            setCompany(newCompany);
            // localStorage.setItem('activeCompany', JSON.stringify(newCompany)); // REMOVE: Using Context state only
            return newCompany;
        } catch (err) {
            console.error('Error creating company:', err);
            setError(err.message || 'Error al crear la empresa');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // updateCompany
    const updateCompany = async (companyData) => {
        setLoading(true);
        setError(null);
        try {
            // Usar el RIF actual de la empresa para actualizar
            if (!company || !company.rif) {
                throw new Error('No hay una empresa seleccionada para actualizar');
            }

            // Enviar la actualización
            await companyService.update(company.rif, companyData);

            // Recargar los datos completos desde la API para asegurar sincronización
            const refreshedCompany = await companyService.getByRif(company.rif);

            setCompany(refreshedCompany);
            // localStorage.setItem('activeCompany', JSON.stringify(refreshedCompany)); // REMOVE: Using Context state only
            return refreshedCompany;
        } catch (err) {
            console.error('Error updating company:', err);
            setError(err.message || 'Error al actualizar la empresa');
            throw err;
        } finally {
            setLoading(false);
        }
    };


    // getCompanyRif
    const getCompanyRif = useCallback(() => {
        if (company && company.rif) {
            return company.rif;
        }
        // RIF por defecto
        return 'J000000000';
    }, [company]);

    // getCompanyId
    const getCompanyId = useCallback(() => {
        if (company && company.id) {
            return company.id;
        }
        // ID por defecto
        return 1;
    }, [company]);

    // Cargar al montar o al autenticar
    useEffect(() => {
        if (authLoading) return; // Wait for auth to settle

        if (isAuthenticated) {
            fetchCompany();
        } else {
            setCompany(null);
        }
    }, [isAuthenticated, authLoading, fetchCompany]);

    // Upload Logo
    const uploadLogo = async (file) => {
        setLoading(true);
        setError(null);
        try {
            if (!company || !company.rif) throw new Error('No hay una empresa activa');
            await companyService.uploadLogo(company.rif, file);
            await fetchCompany(); // Recargar datos para tener los links actualizados
        } catch (err) {
            console.error('Error uploading logo:', err);
            setError(err.message || 'Error al cargar el logo');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Upload Signature
    const uploadSignature = async (file) => {
        setLoading(true);
        setError(null);
        try {
            if (!company || !company.rif) throw new Error('No hay una empresa activa');
            await companyService.uploadSignature(company.rif, file);
            await fetchCompany();
        } catch (err) {
            console.error('Error uploading signature:', err);
            setError(err.message || 'Error al cargar la firma');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Upload Seal
    const uploadSeal = async (file) => {
        setLoading(true);
        setError(null);
        try {
            if (!company || !company.rif) throw new Error('No hay una empresa activa');
            await companyService.uploadSeal(company.rif, file);
            await fetchCompany();
        } catch (err) {
            console.error('Error uploading seal:', err);
            setError(err.message || 'Error al cargar el sello');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Delete Logo
    const deleteLogo = async () => {
        setLoading(true);
        setError(null);
        try {
            if (!company || !company.rif) throw new Error('No hay una empresa activa');
            await companyService.deleteLogo(company.rif);
            await fetchCompany();
        } catch (err) {
            console.error('Error deleting logo:', err);
            setError(err.message || 'Error al eliminar el logo');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Delete Signature
    const deleteSignature = async () => {
        setLoading(true);
        setError(null);
        try {
            if (!company || !company.rif) throw new Error('No hay una empresa activa');
            await companyService.deleteSignature(company.rif);
            await fetchCompany();
        } catch (err) {
            console.error('Error deleting signature:', err);
            setError(err.message || 'Error al eliminar la firma');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Delete Seal
    const deleteSeal = async () => {
        setLoading(true);
        setError(null);
        try {
            if (!company || !company.rif) throw new Error('No hay una empresa activa');
            await companyService.deleteSeal(company.rif);
            await fetchCompany();
        } catch (err) {
            console.error('Error deleting seal:', err);
            setError(err.message || 'Error al eliminar el sello');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    // Get Settings (exposed helper)
    const getSettings = async () => {
        if (!company || !company.rif) return null;
        try {
            return await companyService.getSettings(company.rif);
        } catch (err) {
            console.error('Error fetching settings:', err);
            return null;
        }
    };

    return (
        <CompanyContext.Provider value={{
            company,
            loading,
            error,
            fetchCompany,
            createCompany,
            updateCompany,
            getCompanyRif,
            getCompanyId,
            uploadLogo,
            uploadSignature,
            uploadSeal,
            deleteLogo,
            deleteSignature,
            deleteSeal,
            getSettings
        }}>
            {children}
        </CompanyContext.Provider>
    );
};
